import api from './api';
import cacheService from './cacheService';
import networkService from './networkService';

/**
 * Sync Service für Background-Synchronisation von Offline-Änderungen
 */
class SyncService {
  constructor() {
    this.isSyncing = false;
    this.listeners = [];
    
    // Automatische Synchronisation wenn wieder online
    networkService.addListener((isOnline) => {
      if (isOnline && !this.isSyncing) {
        console.log('🔄 Network back online, starting sync...');
        this.syncAll();
      }
    });
  }

  /**
   * Registriert einen Listener für Sync-Status-Änderungen
   */
  addListener(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Benachrichtigt alle Listener über Sync-Status
   */
  notifyListeners(status) {
    this.listeners.forEach(callback => {
      try {
        callback(status);
      } catch (error) {
        console.error('❌ Sync listener error:', error);
      }
    });
  }

  /**
   * Synchronisiert alle ausstehenden Operationen
   */
  async syncAll() {
    if (this.isSyncing) {
      console.log('⏳ Sync already in progress');
      return;
    }

    if (!networkService.getIsOnline()) {
      console.log('📴 Offline, cannot sync');
      return;
    }

    this.isSyncing = true;
    this.notifyListeners({ status: 'syncing', progress: 0 });

    try {
      const queue = await cacheService.getSyncQueue();
      console.log(`🔄 Starting sync of ${queue.length} operations`);

      if (queue.length === 0) {
        console.log('✅ Nothing to sync');
        this.notifyListeners({ status: 'idle' });
        this.isSyncing = false;
        return;
      }

      let successCount = 0;
      let failCount = 0;

      for (let i = 0; i < queue.length; i++) {
        const operation = queue[i];
        try {
          await this.syncOperation(operation);
          await cacheService.removeFromSyncQueue(operation.id);
          successCount++;
          
          const progress = ((i + 1) / queue.length) * 100;
          this.notifyListeners({ 
            status: 'syncing', 
            progress,
            current: i + 1,
            total: queue.length
          });
        } catch (error) {
          console.error(`❌ Failed to sync operation ${operation.id}:`, error);
          failCount++;
        }
      }

      console.log(`✅ Sync complete: ${successCount} success, ${failCount} failed`);
      
      if (failCount === 0) {
        await cacheService.setLastSync();
        // Offline-Änderungen nach erfolgreichem Sync löschen
        await cacheService.clearOfflineChanges();
      }

      this.notifyListeners({ 
        status: 'completed', 
        successCount, 
        failCount 
      });
    } catch (error) {
      console.error('❌ Sync error:', error);
      this.notifyListeners({ status: 'error', error: error.message });
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Synchronisiert eine einzelne Operation
   */
  async syncOperation(operation) {
    console.log(`🔄 Syncing operation: ${operation.type}`, operation);

    switch (operation.method) {
      case 'POST':
        await api.post(operation.endpoint, operation.data);
        break;
      case 'PUT':
        await api.put(operation.endpoint, operation.data);
        break;
      case 'DELETE':
        await api.delete(operation.endpoint);
        break;
      default:
        throw new Error(`Unknown method: ${operation.method}`);
    }

    console.log(`✅ Synced operation: ${operation.type}`);
  }

  /**
   * Gibt die Anzahl der ausstehenden Operationen zurück
   */
  async getPendingCount() {
    const queue = await cacheService.getSyncQueue();
    return queue.length;
  }

  /**
   * Gibt den Status zurück ob gerade synchronisiert wird
   */
  getIsSyncing() {
    return this.isSyncing;
  }
}

export default new SyncService();

